import React, { useEffect, useState, useRef, useCallback } from "react";
import { Heart, ChefHat, ArrowRight, Sparkles, Flame } from "lucide-react";
import api, { API } from "@/lib/api";
import RecipeOfTheWeekModal from "./RecipeOfTheWeekModal";

const MEAL_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" };
const AUTO_MS = 6000; // 6-second auto-advance

// Rolling Recipe-of-the-Week banner. Beta feedback (Feb 26 2026): "make this
// pro / wow like the rest". We serve up to 5 recently approved recipes as a
// cinematic auto-rotating banner — background photo (blurred), gradient
// overlay, big display title, meal + macro chips, dot pagination, animated
// progress bar. Pauses on hover; clicking anywhere opens the recipe modal.
export default function RecipeOfTheWeekButton() {
  const [recipes, setRecipes] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [i, setI] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());
  const touchStart = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/community-recipes/top");
        const list = data?.recipes?.length ? data.recipes : (data?.recipe ? [data.recipe] : []);
        setRecipes(list);
        setNewCount(data?.new_count || 0);
      } catch { /* silent — banner just hides */ }
    })();
  }, []);

  const advance = useCallback(() => {
    setI((n) => (recipes.length ? (n + 1) % recipes.length : 0));
  }, [recipes.length]);

  useEffect(() => {
    if (recipes.length < 2 || paused || modalOpen) return;
    startRef.current = Date.now();
    timerRef.current = setTimeout(advance, AUTO_MS);
    return () => clearTimeout(timerRef.current);
  }, [i, recipes.length, paused, modalOpen, advance]);

  if (!recipes.length) return null;
  const recipe = recipes[i];
  const isNew = !!recipe.is_new;
  const macros = [
    recipe.kcal != null && { l: "kcal", v: recipe.kcal, c: "#E2E30A" },
    recipe.protein != null && { l: "P", v: `${recipe.protein}g`, c: "#4C9AFF" },
    recipe.carb != null && { l: "C", v: `${recipe.carb}g`, c: "#34C759" },
    recipe.fat != null && { l: "F", v: `${recipe.fat}g`, c: "#FF9500" },
  ].filter(Boolean);
  const photoUrl = recipe.photo
    ? `${API}/community-recipes/photo/${recipe.photo}?auth=${localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token") || ""}`
    : null;

  // Swipe handling — track touch start X on the banner, advance on release
  // when the delta exceeds a small threshold (Whoop-style).
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) < 40) return;
    setI((n) => {
      const len = recipes.length;
      return dx < 0 ? (n + 1) % len : (n - 1 + len) % len;
    });
  };

  return (
    <>
      <section
        data-testid="recipe-of-the-week-banner"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        className="mt-8 relative overflow-hidden border border-white/10 bg-[#141414] group"
        style={{ boxShadow: `inset 3px 0 0 0 #E2E30A` }}>
        <button
          type="button" data-testid="recipe-of-the-week-cta"
          onClick={() => setModalOpen(true)}
          className="w-full text-left relative">
          {/* Background photo — blurred + darkened so the title reads clean */}
          <div className="absolute inset-0 pointer-events-none">
            {photoUrl ? (
              <img key={recipe.id + "-bg"} src={photoUrl} alt="" aria-hidden="true"
                className="w-full h-full object-cover opacity-40 scale-105 blur-[2px] transition-opacity duration-700"
                style={{ animation: "fadeInBanner 0.6s ease" }} />
            ) : (
              <div className="w-full h-full opacity-[0.35]"
                style={{ background: "radial-gradient(circle at 20% 30%, #E2E30A44 0%, transparent 55%), radial-gradient(circle at 80% 70%, #4C9AFF33 0%, transparent 55%)" }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent" />
          </div>

          <div className="relative px-4 sm:px-5 py-4 sm:py-5 flex items-center gap-3 sm:gap-4 min-h-[110px]">
            <span className="grid place-items-center h-11 w-11 sm:h-12 sm:w-12 shrink-0 bg-[#E2E30A]">
              <ChefHat className="h-5 w-5 sm:h-6 sm:w-6 text-black" />
            </span>
            <div key={recipe.id} className="min-w-0 flex-1" style={{ animation: "slideInBanner 0.5s ease" }}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#E2E30A] font-bold">
                  {isNew ? "Just added" : "Recipe of the week"}
                </span>
                {isNew && (
                  <span data-testid="recipe-of-the-week-new-flag" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-black bg-[#E2E30A] px-1.5 py-0.5">
                    <Sparkles className="h-2.5 w-2.5" /> New
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-wider text-zinc-400">{MEAL_LABEL[recipe.meal] || recipe.meal}</span>
              </div>
              <p data-testid="recipe-of-the-week-name" className="font-display text-lg sm:text-2xl uppercase tracking-tight text-white mt-1 leading-tight line-clamp-1">
                {recipe.name}
              </p>
              {macros.length > 0 && (
                <div className="flex items-baseline gap-3 sm:gap-4 mt-1.5 font-mono text-[11px] tabular-nums">
                  {macros.map((m) => (
                    <span key={m.l} className="inline-flex items-baseline gap-1">
                      <span className="text-white font-bold">{m.v}</span>
                      <span className="text-[9px] uppercase tracking-wider" style={{ color: m.c }}>{m.l}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
              <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold text-[#FF3B30] border border-[#FF3B30]/40 bg-black/40 px-2 py-0.5">
                <Heart className="h-3 w-3 fill-current" /> {recipe.helpful}
              </span>
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-200 group-hover:text-white transition-colors">
                View <ArrowRight className="h-3 w-3 text-[#E2E30A] group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
            <span className="sm:hidden inline-flex items-center gap-1 text-[10px] font-mono font-bold text-[#FF3B30] border border-[#FF3B30]/40 bg-black/50 px-2 py-1 shrink-0">
              <Heart className="h-3 w-3 fill-current" /> {recipe.helpful}
            </span>
          </div>

          {/* Slide dots + +N new chip */}
          {(recipes.length > 1 || newCount > 1) && (
            <div className="relative px-4 sm:px-5 py-2.5 flex items-center gap-3 border-t border-white/5 bg-black/20">
              {recipes.length > 1 && (
                <div data-testid="recipe-banner-dots" className="flex items-center gap-1.5">
                  {recipes.map((_, idx) => (
                    <span key={idx} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setI(idx); }}
                      role="button" tabIndex={0}
                      data-testid={`recipe-banner-dot-${idx}`}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setI(idx); } }}
                      className={`h-1.5 rounded-full transition-all cursor-pointer ${idx === i ? "w-6 bg-[#E2E30A]" : "w-1.5 bg-white/25 hover:bg-white/50"}`} />
                  ))}
                </div>
              )}
              <span className="ml-auto text-[9px] uppercase tracking-[0.22em] text-zinc-400 font-mono">
                {i + 1} <span className="text-zinc-600">/ {recipes.length}</span>
              </span>
              {newCount > 1 && (
                <span data-testid="recipe-new-count-chip"
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-[#E2E30A] border border-[#E2E30A]/40 bg-[#E2E30A]/[0.08] px-2 py-0.5">
                  <Flame className="h-2.5 w-2.5" /> {newCount} new this week
                </span>
              )}
            </div>
          )}
        </button>
      </section>
      <RecipeOfTheWeekModal open={modalOpen} onOpenChange={setModalOpen} recipe={recipe} />
      <style>{`
        @keyframes slideInBanner { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInBanner { from { opacity: 0; } to { opacity: 0.4; } }
      `}</style>
    </>
  );
}
